/** Stable authored font IDs shared by feature labels, annotations and Composer. UI chrome is separate. */
export type AuthoredFontFamilyId =
  | 'inter'
  | 'source-sans-3'
  | 'source-serif-4'
  | 'roboto-condensed'
  | 'manrope'
  | 'ibm-plex-sans'
  | 'dm-sans';

export const AUTHORED_FONT_WEIGHTS = [400, 500, 600, 700] as const;
export interface AuthoredFontFamily {
  readonly id: AuthoredFontFamilyId;
  readonly label: string;
  readonly role: 'condensed-sans' | 'humanist-sans' | 'neutral-sans' | 'serif';
  readonly cssFamily: string;
  readonly italic: boolean;
}

export const AUTHORED_FONT_FAMILIES: readonly AuthoredFontFamily[] = [
  {
    id: 'inter',
    label: 'Inter',
    role: 'neutral-sans',
    cssFamily: 'PlaceioMaps-Inter',
    italic: true,
  },
  {
    id: 'source-sans-3',
    label: 'Source Sans 3',
    role: 'humanist-sans',
    cssFamily: 'PlaceioMaps-Source-Sans',
    italic: true,
  },
  {
    id: 'source-serif-4',
    label: 'Source Serif 4',
    role: 'serif',
    cssFamily: 'PlaceioMaps-Source-Serif',
    italic: true,
  },
  {
    id: 'roboto-condensed',
    label: 'Roboto Condensed',
    role: 'condensed-sans',
    cssFamily: 'PlaceioMaps-Roboto-Condensed',
    italic: true,
  },
  {
    id: 'manrope',
    label: 'Manrope',
    role: 'neutral-sans',
    cssFamily: 'PlaceioMaps-Manrope',
    italic: false,
  },
  {
    id: 'ibm-plex-sans',
    label: 'IBM Plex Sans',
    role: 'humanist-sans',
    cssFamily: 'PlaceioMaps-IBM-Plex-Sans',
    italic: true,
  },
  {
    id: 'dm-sans',
    label: 'DM Sans',
    role: 'neutral-sans',
    cssFamily: 'PlaceioMaps-DM-Sans',
    italic: true,
  },
];

export const AUTHORED_FONT_CSS = Object.fromEntries(
  AUTHORED_FONT_FAMILIES.map(({ id, cssFamily }) => [id, cssFamily]),
) as Readonly<Record<AuthoredFontFamilyId, string>>;

export function isAuthoredFontFamilyId(value: unknown): value is AuthoredFontFamilyId {
  return AUTHORED_FONT_FAMILIES.some(({ id }) => id === value);
}

export function supportsAuthoredItalic(family: AuthoredFontFamilyId): boolean {
  return AUTHORED_FONT_FAMILIES.find(({ id }) => id === family)!.italic;
}
